import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from "@angular/common/http";
import { Observable, tap, throwError, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { StoreService } from "../services/store.service";
import { LoaderService } from "../services/loader.service";
import { AuthService } from "src/app/auth/auth.service";
import { GlobalService } from "../services/global.service";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";
import { ErrorReporting } from "../common.interface";
import { CommonService } from "../services/common.service";
import { GtmService } from "src/app/shared/services/gtm-service/gtm.service";
import { GTM_EVENTS, PLATFORM_ENUM } from "src/app/shared/services/gtm-service/gtm-events";
import { LogoutService } from "../services/logout.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    urlParts: any;
    endpoint: any;
    skippedDomainInCaseOfError: any;
    constructor(
        public dialog: MatDialog,
        public toastr: ToastrService,
        public store: StoreService,
        public loaderService: LoaderService,
        private authService: AuthService,
        public globalService: GlobalService,
        private commonService: CommonService,
        private logoutService: LogoutService,
        private gtmService: GtmService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.urlParts = request.url.split("/");
        const fullUrl = new URL(request.url);
        this.endpoint = fullUrl.pathname;

        this.skippedDomainInCaseOfError = [
            "utils.follow.whistle.mobi",
            "thoughversetechnologiespvtltd10422038.o18.click",
            "affiliates.adsplay.in",
            "prudigital99.gotrackier.com",
            "spectrum.trackier.co",
            "affiliates.adsplay.in",
            "track.oodigioo.in",
            "track.vcommission.com",
            "trk.rvrlinks.com",
            "paychat.fuse-ad.com",
            "error-reporting-api.klinq.co.in",
        ];

        // Retrieve tokens from StoreService
        let token = this.store.get("token");
        let mobileToken = this.store.get("m-token");
        token = token && token !== "null" && token !== "undefined" ? token : mobileToken;

        // Base headers
        let header: any = {
            api_version: environment.api_version,
            deviceName: "default_chrome",
            deviceId: "default_ddef7930fd892ba1",
            browserName: "default_chrome",
            platform: "3",
            appVersion: "1.0.21",
            authorization: `Bearer ${token}`,
            latitude: "",
            longitude: "",
            lang: "en",
            appStore: PLATFORM_ENUM.web_app,
        };

        // Add dynamic latitude and longitude from LocationService
        const latitude = this.store.get("latitude");
        const longitude = this.store.get("longitude");

        if (latitude && longitude) {
            header.latitude = latitude;
            header.longitude = longitude;
        }

        // Add dynamic browser name from GlobalService
        let deviceName = this.globalService.getDeviceName();

        // Add dynamic device name from GlobalService
        let browserData = this.globalService.getBrowserNameAndVersion();
        if (Object.keys(browserData).length && deviceName) {
            header.deviceName = `${deviceName}: (${browserData.browserName})`;
            header.deviceOs = browserData.browserVersion;
        }

        // Add cookies as headers
        const cookies = document.cookie.split(";").reduce(
            (acc, cookie) => {
                const [key, value] = cookie.split("=").map((c) => c.trim());
                acc[key] = value;
                return acc;
            },
            {} as Record<string, string>
        );

        // Include specific cookies in headers if needed
        if (cookies["trackingId"]) {
            header["trackingId"] = cookies["trackingId"];
        }
        if (cookies["clientIp"]) {
            header["clientIp"] = cookies["clientIp"];
        }
        if (cookies["referrer"]) {
            header["comeFromReferrer"] = cookies["referrer"];
        }
        if (cookies["deviceId"]) {
            header["deviceId"] = cookies["deviceId"];
        }
        if (this.skippedDomainInCaseOfError.includes(this.urlParts[2])) {
            header = {};
        }
        return next
            .handle(
                request.clone({
                    setHeaders: header,
                })
            )
            .pipe(
                tap({
                    next: (event) => {
                        // if (event instanceof HttpResponse) {
                        //   if (event.status == 401) {
                        //     this.store.remove('accessToken');

                        //     this.router.navigate(['auth/mobile']);
                        //     this.loaderService.hide();
                        //   }
                        // }
                        return event;
                    },
                }),
                catchError((error) => {
                    if (error.status === 401 || error.status === 403) {
                        const refreshToken = this.store.get("refreshToken");
                        return this.authService.refreshToken("" + refreshToken).pipe(
                            switchMap((res: any) => {
                                if (res.success && res.data.accessToken) {
                                    const newToken = res.data.accessToken;
                                    this.store.set({
                                        key: "token",
                                        value: newToken,
                                    });
                                    const updatedHeaders = {
                                        ...header,
                                        authorization: `Bearer ${newToken}`,
                                    };
                                    const clonedRequest = request.clone({
                                        setHeaders: updatedHeaders,
                                    });
                                    return next.handle(clonedRequest);
                                } else {
                                    this.logoutService.logout(false);
                                    // this.toastr.error("Session expired, please login again.");
                                    return throwError(() => "Session expired, please login again");
                                }
                            }),
                            catchError((refreshError) => {
                                this.logoutService.logout(false);
                                return throwError(() => refreshError);
                            })
                        );
                    }
                    this.handleError(error);
                    return throwError(() => error);
                })
            );
    }

    private handleError(error: any) {
        const fullUrl = new URL(error.url);

        if (["/api/error/create"].includes(fullUrl.pathname)) {
            // Skip error reporting for this endpoint
        } else if (error.status === 404) {
            this.toastr.error("Page Not Found!");
            this.loaderService.hide();
            this.errorReporting(error);

            this.gtmService.trackEventWithError(
                GTM_EVENTS.USER_DECLINE.error_popup,
                {
                    user_id: this.store.get("customerId") || "",
                    application_id: this.store.get("applicationId") || "",
                    error_status: error.status,
                    endpoint: fullUrl.pathname,
                },
                error
            );
        } else if (
            !["/app/auth/customer-location"].includes(fullUrl.pathname) &&
            [500, 502, 503, 504, 0].includes(error.status)
        ) {
            this.loaderService.hide();
            this.dialog.closeAll();
            this.globalService.serverDown();
            this.errorReporting(error, true);
        } else {
            if (this.urlParts?.length > 2 && this.skippedDomainInCaseOfError.includes(this.urlParts[2])) {
                console.log("Error Skipped in case of 3rd party API call");
            } else {
                console.log(":::error", error.error.message);
                console.log(":::this.endpoint", fullUrl.pathname);
                if (["CATCH_ERROR", "Catch error", "INTERNAL_ERROR"].includes(error.error.messageSlug)) {
                    this.globalService.catchError();
                } else if (
                    ![
                        "/app/auth/mobile/otp/verify",
                        "/app/work-email/verifyOTP",

                        "/app/ckyc/customer/ckyc/otp/send",
                        "/app/company/search",
                        "/app/netbanking/account/aggregator/url",
                        "/app/app-profile-settings/account/remove",
                        "/app/app-profile-settings/sensitive/data/remove",
                        "/app/app/query/create",
                        "/app/coupon-redemption/redeem",
                        "/app/auth/pan-pro/kyc",
                        "/app/work-email/sendOTP",
                        "/app/auth/customer-location",
                        "/app/pan-verification/panNumber",
                        "/app/marketing/user",
                        "/app/customer-profile/email/verify",
                        "/app/ckyc/customer/ckyc/otp/verify",
                        "/app/auth/register/email/otp/send",
                        "/app/auth/register/email/otp/verify",
                        "/app/dtc/submit/otp",
                        "/app/auth/mobile/otp/sent",
                        "/app/v1/loan-module/apply/loan"
                    ].includes(fullUrl.pathname) &&
                    error.error.message != "null" 
                    && error.error.messageSlug !="NOT_AUTHORIZED"
                ) {
                    this.toastr.error(error.error.message);
                }
                this.loaderService.hide();
            }
            this.errorReporting(error);
        }
    }

    errorReporting(error?: any, isServerDown = false) {
        const userDetails = JSON.parse(this.store.get("customerDetails") ?? "{}");
        const applicationId = this.store.get("applicationId");
        const errorData: ErrorReporting = {
            customerId: parseInt(userDetails?.customerId ?? "0") || 0,
            applicationId: parseInt(applicationId ?? "0") || 0,
            errorDetail: {
                isServerDown: isServerDown,
                extraDetails: error ? error : "",
            },
            apiPoint: this.endpoint,
        };
        this.commonService.errorReporting(errorData).subscribe();
    }
}
